import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import {
  ShieldCheck, Clock, Trash, DownloadSimple, MagnifyingGlass, CircleNotch,
  ChatCircleDots, Phone, ListChecks, CheckCircle, GlobeHemisphereWest, Eye, Scales, Lock,
} from "@phosphor-icons/react";

const inputCls = "w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none transition-all";

const ACTION_LABELS = {
  "user.create": "Skapade användare", "user.update": "Uppdaterade användare", "user.delete": "Tog bort användare",
  "user.reset_password": "Återställde lösenord", "agent.delete": "Tog bort agent",
  "integration.save": "Sparade integration", "integration.disconnect": "Kopplade från integration",
  "compliance.retention_update": "Ändrade gallringsregel", "gdpr.export": "Exporterade kunddata", "gdpr.erase": "Raderade kunddata",
  "agent.clone": "Klonade agent",
};

export default function Compliance() {
  const { user } = useAuth();
  const [auditNonce, setAuditNonce] = useState(0);
  if (!can(user, "manage_compliance")) return <Navigate to="/app" replace />;
  const bumpAudit = () => setAuditNonce((n) => n + 1);
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="compliance-page">
      <div>
        <h1 className="font-display font-extrabold text-2xl flex items-center gap-2">
          <ShieldCheck size={26} weight="fill" className="text-[#0033FF]" /> Efterlevnad & GDPR
        </h1>
        <p className="text-sm text-zinc-500 mt-1">Hantera datalagring, radera kunddata på begäran och följ upp vem som gjort vad.</p>
      </div>
      <EUAIActCard />
      <RetentionCard onChanged={bumpAudit} />
      <ErasureCard onChanged={bumpAudit} />
      <AuditCard nonce={auditNonce} />
    </div>
  );
}

function EUAIActCard() {
  const measures = [
    { icon: Eye, title: "AI-transparens", text: "Kunder informeras tydligt om att de chattar med en AI-assistent (kan slås på per agent under Inställningar)." },
    { icon: ChatCircleDots, title: "Mänsklig överlämning", text: "Kunden kan när som helst be att få prata med en människa — ingen inlåsning i automatiserade beslut." },
    { icon: Lock, title: "PII-maskering", text: "Känsliga personuppgifter maskeras automatiskt innan de behandlas av modellen." },
    { icon: ListChecks, title: "Spårbarhet & audit-logg", text: "Alla administrativa åtgärder loggas med tidsstämpel och användare (se nedan)." },
    { icon: Clock, title: "Datagallring", text: "Konfigurerbar automatisk radering av konversationsdata efter vald period." },
    { icon: Scales, title: "Rätt att bli raderad (GDPR)", text: "Exportera eller radera en kunds samtliga data på begäran." },
  ];
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="eu-ai-act-card">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0033FF]/10 flex items-center justify-center"><Scales size={24} weight="fill" className="text-[#0033FF]" /></div>
          <div>
            <h2 className="font-display font-bold text-lg">EU AI Act-läge</h2>
            <p className="text-sm text-zinc-500">Inbyggda åtgärder för att möta EU:s AI-förordning och stärka kundernas förtroende.</p>
          </div>
        </div>
        <div data-testid="eu-data-badge" className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-3.5 py-2 text-sm font-semibold">
          <GlobeHemisphereWest size={18} weight="fill" /> EU-datalagring
          <span className="inline-flex items-center gap-1 text-emerald-600/80 text-xs"><CheckCircle size={13} weight="fill" /> Aktiv</span>
        </div>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        {measures.map((m) => (
          <div key={m.title} className="flex items-start gap-3 rounded-lg border border-zinc-100 bg-zinc-50/50 p-3.5">
            <div className="h-8 w-8 rounded-lg bg-white border border-zinc-200 flex items-center justify-center shrink-0"><m.icon size={16} weight="bold" className="text-[#0033FF]" /></div>
            <div>
              <div className="font-semibold text-sm flex items-center gap-1.5">{m.title} <CheckCircle size={14} weight="fill" className="text-emerald-500" /></div>
              <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{m.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RetentionCard({ onChanged }) {
  const [days, setDays] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/compliance").then((r) => setDays(r.data.retention_days || 0)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const clamped = Math.max(0, Math.min(Number(days) || 0, 3650));
      const { data } = await api.put("/compliance", { retention_days: clamped });
      setDays(data.retention_days);
      toast.success(data.retention_days > 0 ? `Konversationer gallras efter ${data.retention_days} dagar` : "Automatisk gallring avstängd");
      onChanged?.();
    } catch { toast.error("Kunde inte spara"); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6">
      <h2 className="font-display font-bold text-lg flex items-center gap-2"><Clock size={20} weight="fill" className="text-[#0033FF]" /> Datalagring</h2>
      <p className="text-sm text-zinc-500 mt-1 max-w-2xl">Radera automatiskt konversationer (och kopplad kunddata) som är äldre än vald period. Sätt till 0 för att behålla allt.</p>
      <div className="flex items-end gap-3 mt-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-zinc-400">Gallra efter (dagar)</label>
          <input data-testid="retention-days-input" type="number" min={0} max={3650} value={days}
            onChange={(e) => setDays(e.target.value)} disabled={loading} className={`${inputCls} mt-1 w-40`} />
        </div>
        <button data-testid="retention-save" onClick={save} disabled={saving || loading}
          className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-5 py-2.5 font-semibold disabled:opacity-50">
          {saving && <CircleNotch size={16} className="animate-spin" />} Spara
        </button>
      </div>
      <p className="text-xs text-zinc-400 mt-2">Gallringen körs automatiskt i bakgrunden var sjätte timme.</p>
    </div>
  );
}

function ErasureCard({ onChanged }) {
  const [convos, setConvos] = useState([]);
  const [q, setQ] = useState("");
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/conversations").then((r) => setConvos(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportConv = async (c) => {
    setBusy(c.id);
    try {
      const { data } = await api.get(`/gdpr/conversations/${c.id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `gdpr-export-${c.session_id || c.id}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Data exporterad");
      onChanged?.();
    } catch { toast.error("Kunde inte exportera"); } finally { setBusy(null); }
  };

  const erase = async (c) => {
    setBusy(c.id);
    try {
      await api.post(`/gdpr/conversations/${c.id}/erase`);
      setConvos((cs) => cs.filter((x) => x.id !== c.id));
      setConfirmId(null);
      toast.success("Kunddata raderad permanent");
      onChanged?.();
    } catch { toast.error("Kunde inte radera"); } finally { setBusy(null); }
  };

  const filtered = convos.filter((c) => {
    if (!q.trim()) return true;
    const hay = `${c.agent_name || ""} ${c.session_id || ""} ${(c.messages || []).map((m) => m.content).join(" ")}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6">
      <h2 className="font-display font-bold text-lg flex items-center gap-2"><Trash size={20} weight="fill" className="text-[#0033FF]" /> Rätt att bli raderad</h2>
      <p className="text-sm text-zinc-500 mt-1 max-w-2xl">Exportera (rätt till tillgång) eller radera permanent (rätt till radering) en kunds konversation och all kopplad data.</p>
      <div className="relative mt-4 max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input data-testid="erasure-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Sök på agent, session-ID eller innehåll…" className={`${inputCls} pl-9`} />
      </div>
      <div className="mt-4 divide-y divide-zinc-100 max-h-[420px] overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-sm text-zinc-400 py-6 text-center">Inga konversationer.</p>
        ) : filtered.map((c) => {
          const last = c.messages?.[c.messages.length - 1];
          return (
            <div key={c.id} data-testid={`erasure-row-${c.id}`} className="flex items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 font-semibold text-sm">
                  {c.channel === "voice" ? <Phone size={14} weight="bold" className="text-[#0033FF]" /> : <ChatCircleDots size={14} weight="bold" className="text-[#0033FF]" />}
                  {c.agent_name || "Agent"}
                  <span className="text-xs text-zinc-400 font-normal truncate">· {c.session_id}</span>
                </div>
                <p className="text-xs text-zinc-500 mt-0.5 truncate">{last?.content || "—"}</p>
              </div>
              <button data-testid={`erasure-export-${c.id}`} onClick={() => exportConv(c)} disabled={busy === c.id}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-600 hover:text-[#0033FF] border border-zinc-200 rounded-md px-3 py-1.5 disabled:opacity-50">
                <DownloadSimple size={15} weight="bold" /> Exportera
              </button>
              {confirmId === c.id ? (
                <div className="flex items-center gap-1.5">
                  <button data-testid={`erasure-confirm-${c.id}`} onClick={() => erase(c)} disabled={busy === c.id}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md px-3 py-1.5 disabled:opacity-50">
                    {busy === c.id ? <CircleNotch size={15} className="animate-spin" /> : "Bekräfta"}
                  </button>
                  <button onClick={() => setConfirmId(null)} className="text-sm text-zinc-500 px-2">Avbryt</button>
                </div>
              ) : (
                <button data-testid={`erasure-delete-${c.id}`} onClick={() => setConfirmId(c.id)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 border border-red-200 rounded-md px-3 py-1.5">
                  <Trash size={15} weight="bold" /> Radera
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AuditCard({ nonce }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/audit").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [nonce]);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6">
      <h2 className="font-display font-bold text-lg flex items-center gap-2"><ListChecks size={20} weight="fill" className="text-[#0033FF]" /> Granskningslogg</h2>
      <p className="text-sm text-zinc-500 mt-1">Spårar känsliga åtgärder: användarhantering, integrationer, radering och export.</p>
      <div className="mt-4 overflow-x-auto">
        {loading ? (
          <p className="text-sm text-zinc-400 py-6 text-center">Laddar…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-zinc-400 py-6 text-center" data-testid="audit-empty">Inga loggade åtgärder än.</p>
        ) : (
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-400 border-b border-zinc-100">
                <th className="py-2 pr-4 font-bold">Tidpunkt</th>
                <th className="py-2 pr-4 font-bold">Användare</th>
                <th className="py-2 pr-4 font-bold">Åtgärd</th>
                <th className="py-2 font-bold">Detaljer</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-zinc-50">
                  <td className="py-2 pr-4 text-zinc-500 whitespace-nowrap">{new Date(r.ts).toLocaleString("sv-SE")}</td>
                  <td className="py-2 pr-4 text-zinc-700">{r.actor_email}</td>
                  <td className="py-2 pr-4 font-semibold">{ACTION_LABELS[r.action] || r.action}</td>
                  <td className="py-2 text-zinc-500 truncate max-w-xs">{r.target}{r.detail ? ` · ${r.detail}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
